import * as THREE from 'three';
import { GAME } from '../config/gameConfig';
import type { InputManager } from '../core/InputManager';

export interface AABB {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  minY?: number;
  maxY?: number;
  /** 扉など動的に無効化する用 */
  tag?: string;
}

export class PlayerController {
  readonly camera: THREE.PerspectiveCamera;
  readonly position = new THREE.Vector3(0, GAME.playerHeight, 3);
  yaw = 0;
  pitch = 0;
  private velocityY = 0;
  private onGround = true;
  private footTimer = 0;
  enabled = true;

  colliders: AABB[] = [];

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(70, aspect, 0.08, 80);
    this.camera.position.copy(this.position);
  }

  setSpawn(x: number, z: number, yaw = 0): void {
    this.position.set(x, GAME.playerHeight, z);
    this.yaw = yaw;
    this.pitch = 0;
    this.velocityY = 0;
    this.syncCamera();
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, input: InputManager, onFootstep?: () => void): void {
    if (!this.enabled) return;

    const sens = GAME.mouseSensitivityBase * input.sensitivity;
    this.yaw -= input.mouseDX * sens;
    this.pitch -= input.mouseDY * sens;
    this.pitch = Math.max(-1.4, Math.min(1.4, this.pitch));

    const move = input.getMoveVector();
    const speed = move.run ? GAME.runSpeed : GAME.walkSpeed;
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));

    let dx = (right.x * move.x + forward.x * (-move.z)) * speed * dt;
    let dz = (right.z * move.x + forward.z * (-move.z)) * speed * dt;

    // ジャンプ
    if (input.isJump() && this.onGround) {
      this.velocityY = GAME.jumpForce;
      this.onGround = false;
    }
    this.velocityY -= GAME.gravity * dt;
    let dy = this.velocityY * dt;

    // 水平衝突（半径付き）
    const r = GAME.playerRadius;
    const tryPos = this.position.clone();
    tryPos.x += dx;
    if (!this.collides(tryPos.x, this.position.z, r)) {
      this.position.x = tryPos.x;
    } else {
      dx = 0;
    }
    tryPos.x = this.position.x;
    tryPos.z += dz;
    if (!this.collides(this.position.x, tryPos.z, r)) {
      this.position.z = tryPos.z;
    } else {
      dz = 0;
    }

    this.position.y += dy;
    if (this.position.y <= GAME.playerHeight) {
      this.position.y = GAME.playerHeight;
      this.velocityY = 0;
      this.onGround = true;
    }

    // 足音
    const moving = Math.hypot(dx, dz) > 0.0001 && this.onGround;
    if (moving) {
      this.footTimer -= dt;
      if (this.footTimer <= 0) {
        onFootstep?.();
        this.footTimer = move.run ? GAME.footstepInterval * 0.65 : GAME.footstepInterval;
      }
    } else {
      this.footTimer = 0;
    }

    this.syncCamera();
  }

  private collides(x: number, z: number, r: number): boolean {
    for (const c of this.colliders) {
      const minY = c.minY ?? 0;
      const maxY = c.maxY ?? 3;
      // プレイヤー足元〜頭の高さで重なるか
      if (GAME.playerHeight < minY || 0.3 > maxY) continue;
      if (x + r > c.minX && x - r < c.maxX && z + r > c.minZ && z - r < c.maxZ) {
        return true;
      }
    }
    return false;
  }

  private syncCamera(): void {
    this.camera.position.set(this.position.x, this.position.y - (GAME.playerHeight - GAME.eyeHeight), this.position.z);
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
  }

  getLookRaycaster(): THREE.Raycaster {
    const rc = new THREE.Raycaster();
    rc.far = GAME.interactDistance;
    const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    rc.set(this.camera.position, dir);
    return rc;
  }
}
